import { chunk, flatten, isEqual } from 'lodash-es'
import {
  OrderLabourInfo,
  OrderPart,
  SpecsOrderInfo,
  VehicleShort,
} from '../../../src/api/cart/interfaces'
import { SELL_NETWORK_BATCH_SIZE } from 'common'
import { LocationType } from '../../../src/helpers/locationType'
import PartServiceProvider from '../../../src/services/PartServiceProvider'
import {
  Link,
  ToastNotificationVariant,
} from '../../../src/ui-components/util/Interfaces'
import { LaborItem } from '../../../src/store/models/LaborModel'
import {
  AvailabilityError,
  AvailabilityErrorType,
  ProductLocationModel,
  ProductModel,
} from 'interfaces'
import {
  CartMode,
  ShoppingCartProduct,
} from '../../../src/store/models/ShoppingCartModels'
import { Vehicle } from '../../../src/store/models/Vehicles'
import { VehicleSpecificationCartItem } from '../../../src/store/models/VehicleSpecification'
import { IAddToCartNotification } from 'pages/quotes/src/store/interfaces'
import { StoreInstances } from '../../../src/store/StoreInstancesContainer'
import { buildAvailabilityRequest, fetchProduct } from './utils/CartValidations'
import { ISetQtyProps, SellerCartVehicle } from '../src/store/ISellerCart'
import {
  LocationInfoResponse,
  PartInfoRequest,
  PartInfoResponse,
  SellerPartInfoResponse,
} from '../../../src/store/cart/ValidationInterfaces'
import { Config, IS_INTEGRATED_VERSION } from 'src/config/ConfigManager'
import ShowNotification from 'src/ui-components/modals/ToastNotifcationNotiStack'

export const makeNewCartVehicle = (vehicle: Vehicle): SellerCartVehicle => {
  return {
    vehicle,
    sellers: [],
    specifications: [],
    laborResults: [],
    orderFormData: {
      poNumber: '',
      customerName: '',
      noteToStore: '',
      personalNote: '',
      shipToAddress: '',
    },
  }
}

export const getGoogleAnalyticsCategory = (
  allianceTerminologyID: string,
  partDescription: string
): string => {
  // Mapping category to part type description to align with B2C model
  const selectedPartTypeValue =
    StoreInstances.searchStore.findSelectedPartTypeById(
      allianceTerminologyID
    )?.value

  return selectedPartTypeValue || partDescription // Using part description as backup MP4P-1212
}

export const isBuyDirectLocation = (
  location: ProductLocationModel
): boolean => {
  return (
    location?.locType === LocationType.BUY_DIRECT ||
    location?.locType === LocationType.VIRTUAL_BUY_DIRECT
  )
}

export const mapCartVehicleLaborsToLaborsInOrderRequestBody = (
  cartLabors: Array<LaborItem>
): Array<OrderLabourInfo> => {
  const labors: Array<OrderLabourInfo> = []
  cartLabors.forEach((cartLabor) => {
    const orderLabor = {
      details: cartLabor.details,
      hours: cartLabor.hours,
      laborId: cartLabor.laborId,
      description: cartLabor.laborDescription,
      rate: cartLabor.rate,
      notes: cartLabor.miscellaneousText,
      skillLevel: cartLabor.skillLevel,
      warrantyHours: cartLabor.warrantyHrs,
    }
    labors.push(orderLabor)
  })
  return labors
}

export const mapCartVehicleSpecsToSpecsInOrderRequestBody = (
  cartSpecifications: Array<VehicleSpecificationCartItem>
): Array<SpecsOrderInfo> => {
  const specifications: Array<SpecsOrderInfo> = []
  cartSpecifications.forEach((cartSpecification) => {
    const orderSpec = {
      ...cartSpecification,
      miscellaneousTexts: cartSpecification.miscellaneousTexts.join(', '),
      id: 0,
      orderId: 0,
    }
    specifications.push(orderSpec)
  })
  return specifications
}

export const isVehicleComplete = (vehicle: Vehicle): boolean => {
  return (
    vehicle &&
    !!vehicle.year?.id &&
    !!vehicle.make?.id &&
    !!vehicle.model?.id &&
    !!vehicle.modelType?.id &&
    !!vehicle.engine?.id
  )
}

export const updateLocationPrices = (
  oldLocation: ProductLocationModel,
  newLocation: LocationInfoResponse
): ProductLocationModel => {
  const newCost = newLocation?.unitCostPrice ?? 0
  const newCoreCost = newLocation?.unitCorePrice ?? 0
  const newCoreList = newLocation?.unitListCore ?? 0
  const newList = newLocation?.unitListPrice ?? 0

  if (!newLocation) {
    return oldLocation
  }

  return {
    ...oldLocation,
    cost: newCost,
    coreCost: newCoreCost,
    coreList: newCoreList,
    list: newList,
    isSelected: newLocation?.isSelected || false,
    priceBreaks:
      (newLocation?.priceBreaks || [])?.length > 0
        ? newLocation?.priceBreaks
        : oldLocation.priceBreaks,
    schedule: newLocation?.schedule ?? oldLocation.schedule,
  }
}

export const mapLocationInfo = (
  location: LocationInfoResponse
): ProductLocationModel => {
  return {
    isSelected: location.isSelected,
    locationId: location.locationId,
    called: location.called || location.locationDescription,
    cost: location.unitCostPrice ?? 0,
    coreCost: location.unitCorePrice ?? 0,
    coreList: location.unitListCore ?? 0,
    list: location.unitListPrice ?? 0,
    qtyAvailable: location.quantityAvailable ?? 0,
    locType:
      location.locationId === '100'
        ? LocationType.PRIMARY
        : location.locationStatus,
    priceBreaks: location.priceBreaks || [],
    packNumUnits: location.packNumUnits || 1,
    schedule: location.schedule,
  }
}

export const roundQty = (currentQty: number, buyIncrement: number): number => {
  return Math.ceil(currentQty / buyIncrement) * buyIncrement
}

export const updateLocationInfo = (
  oldPart: ProductModel,
  newPart: PartInfoResponse
): ProductModel => {
  const filteredLocations = oldPart.location.filter(
    (location) => Number(location.locationId) >= 100
  )

  const updatedLocations = filteredLocations.map(
    (location): ProductLocationModel => {
      const updatedLocationInfo: LocationInfoResponse = newPart.locations.find(
        (l) => Number(l.locationId) === Number(location.locationId)
      )
      const updatedLocation = updateLocationPrices(
        location,
        updatedLocationInfo
      )

      updatedLocation.locType =
        updatedLocationInfo?.locationId === '100' &&
        updatedLocationInfo?.locationStatus !== LocationType.VIEW_ONLY
          ? LocationType.PRIMARY
          : (updatedLocationInfo?.locationStatus ?? updatedLocation.locType)

      updatedLocation.qtyAvailable = updatedLocationInfo?.quantityAvailable ?? 0
      updatedLocation.minQty = updatedLocationInfo?.minOrderQty ?? 1
      return updatedLocation
    }
  )

  // Add new locations
  newPart.locations.forEach((location) => {
    if (
      !updatedLocations.some(
        (l) => l.locationId.toString() === location.locationId.toString()
      )
    ) {
      updatedLocations.push(mapLocationInfo(location))
    }
  })

  /*
   * MPV3-8623 - Fix store position ordering when adding quote to cart.
   * When a quote is added to cart, the old product only has the saved location(s)
   * (e.g., [100, 103]). After merging with the availability API response,
   * new locations are appended at the end, resulting in incorrect order
   * (e.g., [100, 103, 101, 102, ...] instead of the expected API order).
   * Sorting by the API response order ensures locations display correctly
   * in the "more locations" drawer within the cart.
   */
  const apiOrder = newPart.locations.map((l) => l.locationId.toString())
  updatedLocations.sort((a, b) => {
    const indexA = apiOrder.indexOf(a.locationId.toString())
    const indexB = apiOrder.indexOf(b.locationId.toString())
    return (
      (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB)
    )
  })

  // Return new object instead of mutating oldPart
  return {
    ...oldPart,
    weight: newPart.weight,
    height: newPart.height,
    width: newPart.width,
    length: newPart.length,
    location: updatedLocations,
  }
}

export const updatePartInfo = async (
  oldPart: ProductModel,
  requestedQty: number,
  location: ProductLocationModel,
  coverageKey?: string
): Promise<{ product: ProductModel; hasTimeout: boolean }> => {
  const reqData = buildAvailabilityRequest(oldPart, requestedQty, location)
  const { part: updatedPartInfo, hasTimeout } = await fetchProduct(
    {
      parts: [reqData],
    },
    coverageKey
  )

  if (hasTimeout || !updatedPartInfo) return { product: oldPart, hasTimeout }

  return {
    product: updateLocationInfo(oldPart, updatedPartInfo),
    hasTimeout: false,
  }
}

export const getUpdatedPartsList = async (
  productDetails: ISetQtyProps[]
): Promise<ProductModel[]> => {
  // The sellnetworks are limited by the number of records they can process at a time.
  // So we need to chunk the parts before checking the availability.
  const chunks = chunk(productDetails, SELL_NETWORK_BATCH_SIZE)
  return Promise.all(
    chunks.map((partsChunk) => {
      return PartServiceProvider.getAvailability(
        {
          parts: partsChunk.map(({ product, quantity }) => {
            return buildAvailabilityRequest(product, quantity)
          }),
        },
        partsChunk[0]?.seller?.coverageKey || ''
      )
    })
  ).then((updatedPartChunks) => {
    return flatten(
      updatedPartChunks.map((updatedChunk, index) => {
        const currentChunk = chunks[index]
        return currentChunk.map((currentPart, idx) => {
          return updateLocationInfo(
            currentPart.product,
            updatedChunk.parts[idx]
          )
        })
      })
    )
  })
}

/**
 * Util used to compare a list of parts with the parts added to a cart and display a notification based on what was added to the cart.
 * @param products The list of products being compared.
 * @param inCartProducts The list of products in the cart.
 * @param laborResults The array of labour results being compared with the cart.
 * @param specifications The array of specifications being compared with the cart.
 * @param viewCartLink The object to add link in the toast notification.
 */
export const showCartPartComparisionNotification = (
  products: ShoppingCartProduct[],
  inCartProducts: ShoppingCartProduct[],
  laborResults: LaborItem[],
  specifications: VehicleSpecificationCartItem[],
  viewCartLink: Link
): void => {
  const addToCartNotification: IAddToCartNotification = {
    toastNotificationType: ToastNotificationVariant.WARNING,
    messages: [],
  }

  const cartProducts = products
    .map((p) =>
      inCartProducts.find(
        (cartProduct) =>
          cartProduct.partNumber === p.partNumber &&
          cartProduct.lineCode === p.lineCode
      )
    )
    .filter((p) => p) // removing undefined items from previous map

  const productsWithErrors = cartProducts?.filter((p) => p?.errors?.length)

  const cartHiddenProducts = productsWithErrors?.filter((p) =>
    p?.errors?.find(
      (e) =>
        e.errorType === AvailabilityErrorType.CANT_BE_FULFILLED ||
        e.errorType === AvailabilityErrorType.MIN_QTY_CHANGE
    )
  )

  if (!cartProducts || cartHiddenProducts?.length === cartProducts?.length) {
    if (
      laborResults &&
      specifications &&
      laborResults.length &&
      specifications.length
    ) {
      addToCartNotification.messages.push('onlySpecsLaborResultsAreAddedToCart')
    } else if (laborResults && laborResults.length) {
      addToCartNotification.messages.push('onlyLaborResultsAreAddedToCart')
    } else if (specifications && specifications.length) {
      addToCartNotification.messages.push('onlySpecsAreAddedToCart')
    } else {
      addToCartNotification.toastNotificationType =
        ToastNotificationVariant.ERROR
      addToCartNotification.messages.push('noPartsFoundAddedToCart')
    }
  } else if (productsWithErrors?.length) {
    addToCartNotification.messages.push('issueInAddingPartsToCart')
  } else {
    addToCartNotification.toastNotificationType =
      ToastNotificationVariant.SUCCESS
    addToCartNotification.messages.push('productsWereAddedToTheCart')
  }
  switch (addToCartNotification.toastNotificationType) {
    case ToastNotificationVariant.SUCCESS:
      ShowNotification({
        message: addToCartNotification?.messages?.[0],
        variant: ToastNotificationVariant.SUCCESS,
        link: viewCartLink,
      })
      break
    case ToastNotificationVariant.ERROR:
      ShowNotification({
        message: addToCartNotification?.messages?.[0],
        variant: ToastNotificationVariant.ERROR,
      })
      break
    case ToastNotificationVariant.WARNING:
      for (const eachMessage of addToCartNotification.messages) {
        ShowNotification({
          message: eachMessage,
          variant: ToastNotificationVariant.WARNING,
          link: viewCartLink,
        })
      }
      break
    default:
      break
  }
}

const isFindItOffered = (): boolean => {
  const prefs = StoreInstances.userStore?.preferences
  if (!prefs) {
    return false
  }
  return (
    prefs.cart_locPartMsg === 'true' && prefs.findit_orderIfNotAvail === 'true'
  )
}

export const isLocatePartMsg = (orderPart: OrderPart): boolean => {
  if (isFindItOffered()) {
    if (orderPart.locationId?.toString() === '100') {
      if (orderPart.quantityAvailable < orderPart.quantityRequested) {
        return true
      }
    }
  }
  return false
}

export const isMatchingVehicle = (
  vehicle1: VehicleShort,
  vehicle2: Vehicle
): boolean => {
  return (
    vehicle1.year === vehicle2.year?.value &&
    vehicle1.make === vehicle2.make?.value &&
    vehicle1.model === vehicle2.model?.value &&
    vehicle1.engine === vehicle2.engine?.value
  )
}

export const hasSameErrors = (
  previousCartErrors: AvailabilityError[],
  updatedCartErrors: AvailabilityError[]
): boolean => {
  return isEqual(previousCartErrors, updatedCartErrors)
}

export const flattenCartErrors = (
  sellerCartVehicles: SellerCartVehicle[]
): Array<AvailabilityError> => {
  return sellerCartVehicles.flatMap((sellerCartVehicle) => [
    ...sellerCartVehicle.sellers.flatMap((seller) =>
      seller.locations.flatMap(
        (location) => location.parts.flatMap((part) => part.errors) // Flatten the errors in each CartProduct
      )
    ),
    ...(sellerCartVehicle.productsCantbeFulfilled?.flatMap(
      (product) => product.errors
    ) || []), // Flatten errors in productsCantbeFulfilled, if present
  ])
}

export interface PartsAvailabilityResult {
  parts: PartInfoResponse[]
  hasTimeout: boolean
}

export const getPartsAvailability = async (
  productDetails: PartInfoRequest[],
  coverageKey?: string
): Promise<PartsAvailabilityResult> => {
  // The sellnetworks are limited by the number of records they can process at a time.
  // So we need to chunk the parts before checking the availability.
  const chunks = chunk(productDetails, SELL_NETWORK_BATCH_SIZE)
  const results = await Promise.all(
    chunks.map((partsChunk) => {
      return PartServiceProvider.getAvailability(
        { parts: partsChunk },
        coverageKey
      )
    })
  )
  const allParts = flatten(results.map((r) => r.parts))
  const hasTimeout = results.some((r) => r.snwPrimaryStatus === 'TIMEOUT')
  return { parts: allParts, hasTimeout }
}

/**
 * Products match if the partNumber, lineCode, description all match
 */
export const isCartSellerProduct = (
  p1: ProductModel,
  p2: SellerPartInfoResponse
): boolean => {
  return (
    p1.partNumber === p2.partNumber &&
    p1.lineCode === p2.lineCode &&
    p1.description === p2.description
  )
}

export const getCartMode = (): CartMode => {
  if (!Config.isNotCartOnlyMode) {
    return CartMode.CART_ONLY
  }
  if (IS_INTEGRATED_VERSION) {
    return CartMode.SINGLE_VEHICLE
  }
  if (StoreInstances.userStore.preferences.cart_multiCart === 'true') {
    return CartMode.MULTI_VEHICLE
  }
  return CartMode.SINGLE_VEHICLE
}
